/**
 * Backend Configuration Toggle
 *
 * Switch between FastAPI (main) and Django (legacy) backends
 */

export const BACKEND_CONFIG = {
  // ⚠️ TOGGLE THIS TO SWITCH BACKENDS ⚠️
  USE_FASTAPI: true, // FastAPI is now the main backend

  // Port configuration
  FASTAPI_PORT: 8000, // Main backend
  DJANGO_PORT: 8001,  // Legacy backend

  // Get the appropriate port based on backend selection
  get apiPort(): number {
    return this.USE_FASTAPI ? this.FASTAPI_PORT : this.DJANGO_PORT;
  },

  // Check if using FastAPI
  get isFastAPI(): boolean {
    return this.USE_FASTAPI;
  },

  // Get backend name for logging/debugging
  get backendName(): string {
    return this.USE_FASTAPI ? 'FastAPI' : 'Django';
  },

  // Endpoint path mappings (FastAPI has different paths)
  endpoints: {
    // Authentication - FastAPI uses /api/internal/auth prefix
    login: (useFastAPI: boolean) => useFastAPI ? '/api/internal/auth/login' : '/auth/login',
    logout: (useFastAPI: boolean) => useFastAPI ? '/api/internal/auth/logout' : '/auth/logout',
    refresh: (useFastAPI: boolean) => useFastAPI ? '/api/internal/auth/refresh' : '/auth/refresh',
    me: (useFastAPI: boolean) => useFastAPI ? '/api/internal/auth/me' : '/auth/user/',

    // Time tracking (different paths)
    clockIn: (useFastAPI: boolean) => useFastAPI ? '/api/internal/time-tracking/clock-in' : '/time-log/clock-in',
    clockOut: (useFastAPI: boolean) => useFastAPI ? '/api/internal/time-tracking/clock-out' : '/time-log/clock-out',
    currentSession: (useFastAPI: boolean) => useFastAPI ? '/api/internal/time-tracking/current' : '/time-log/current',
    timeLogHistory: (useFastAPI: boolean) => useFastAPI ? '/api/internal/time-tracking/history' : '/time-log/history',
    clockStatus: (useFastAPI: boolean) => useFastAPI ? '/api/internal/time-tracking/status' : '/time-log/status',
    breakStart: (useFastAPI: boolean) => useFastAPI ? '/api/internal/time-tracking/break/start' : '/time-log/break/start',
    breakEnd: (useFastAPI: boolean) => useFastAPI ? '/api/internal/time-tracking/break/end' : '/time-log/break/end',

    // Staff Management
    staffList: (useFastAPI: boolean) => useFastAPI ? '/api/internal/staff' : '/roles/staff/all',
    staffDetail: (useFastAPI: boolean, id: string) => useFastAPI ? `/api/internal/staff/${id}` : `/roles/staff/${id}`,
    staffCreate: (useFastAPI: boolean) => useFastAPI ? '/api/internal/staff' : '/roles/staff/create',
    staffUpdate: (useFastAPI: boolean, id: string) => useFastAPI ? `/api/internal/staff/${id}` : `/roles/staff/${id}/update`,
    staffCount: (useFastAPI: boolean) => useFastAPI ? '/api/internal/staff/count' : '/roles/staff/count',

    // Roles
    rolesList: (useFastAPI: boolean) => useFastAPI ? '/api/internal/roles' : '/roles/',
    roleDetail: (useFastAPI: boolean, id: string) => useFastAPI ? `/api/internal/roles/${id}` : `/roles/${id}`,

    // Staff-Role Assignment
    assignRole: (useFastAPI: boolean, staffId: string, roleId: string) => useFastAPI ? `/api/internal/staff/${staffId}/roles/${roleId}` : '/roles/assign',
    removeRole: (useFastAPI: boolean, staffId: string, roleId: string) => useFastAPI ? `/api/internal/staff/${staffId}/roles/${roleId}` : '/roles/remove',
    staffRoles: (useFastAPI: boolean, staffId: string) => useFastAPI ? `/api/internal/staff/${staffId}/roles` : `/roles/staff/${staffId}`,

    // Facilities - Locations
    staffLocations: (useFastAPI: boolean) => useFastAPI ? '/api/internal/facilities/staff/locations/' : '/facilities/staff/locations/',

    // Facilities - Departments
    departmentsList: (useFastAPI: boolean) => useFastAPI ? '/api/internal/facilities/departments/' : '/queue/departments',

    // Facilities - Devices
    deviceRoom: (useFastAPI: boolean) => useFastAPI ? '/api/internal/facilities/devices/room/' : '/facilities/devices/room/',
    deviceRoomByIP: (useFastAPI: boolean) => useFastAPI ? '/api/internal/facilities/devices/room-by-ip/' : '/facilities/devices/room-by-ip/',

    // Admin - Authentication
    adminLogin: (useFastAPI: boolean) => useFastAPI ? '/api/admin/auth/login' : '/api/admin/auth/login',
    adminLogout: (useFastAPI: boolean) => useFastAPI ? '/api/admin/auth/logout' : '/api/admin/auth/logout',

    // Admin - Tenant Management
    tenantsList: (useFastAPI: boolean) => useFastAPI ? '/api/admin/tenants/' : '/api/admin/tenants/',
    tenantDetail: (useFastAPI: boolean, id: number) => useFastAPI ? `/api/admin/tenants/${id}` : `/api/admin/tenants/${id}/`,
    tenantCreate: (useFastAPI: boolean) => useFastAPI ? '/api/admin/tenants/' : '/api/admin/tenants/create/',
    tenantUpdate: (useFastAPI: boolean, id: number) => useFastAPI ? `/api/admin/tenants/${id}` : `/api/admin/tenants/${id}/update/`,
    tenantDelete: (useFastAPI: boolean, id: number) => useFastAPI ? `/api/admin/tenants/${id}/delete/` : `/api/admin/tenants/${id}/delete/`,
    tenantStats: (useFastAPI: boolean) => useFastAPI ? '/api/admin/stats/' : '/api/admin/stats/',
  },

  // Get tenant header name (FastAPI uses X-Tenant, Django uses X-Tenant-Domain)
  getTenantHeader(): string {
    return this.USE_FASTAPI ? 'X-Tenant' : 'X-Tenant-Domain';
  },

  // Get tenant value from hostname
  getTenantValue(): string {
    const hostname = window.location.hostname;

    if (this.USE_FASTAPI) {
      // Extract subdomain from hostname
      const subdomain = hostname.split('.')[0];

      // Development: localhost domains
      if (hostname.includes('.localhost')) {
        return subdomain;  // anandlab.localhost -> anandlab
      }

      // Plain localhost (admin portal) - use public schema
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return 'public';
      }

      // Admin portal patterns - use public schema
      // www.domain.com, admin.domain.com, or just domain.com (no subdomain)
      if (subdomain === 'www' || subdomain === 'admin' || !hostname.includes('.') || hostname.split('.').length === 2) {
        return 'public';
      }

      // Production: subdomain is the tenant schema name
      // Examples: aidc.domain.com -> aidc, clinic1.domain.com -> clinic1
      return subdomain;
    } else {
      // Django uses full hostname
      return hostname;
    }
  }
};

// Log current backend configuration on load
console.log(`🔧 Backend Configuration: ${BACKEND_CONFIG.backendName} on port ${BACKEND_CONFIG.apiPort}`);
if (BACKEND_CONFIG.isFastAPI) {
  console.log(`🚀 Using FastAPI (Main Backend) - Tenant: ${BACKEND_CONFIG.getTenantValue()}`);
} else {
  console.log(`⚠️  Using Django (Legacy Backend) - Consider migrating to FastAPI`);
}
